import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import {
  FaPlus, FaTimes, FaEdit, FaTrashAlt, FaEye, FaEyeSlash,
  FaSave, FaDownload, FaUpload, FaImage, FaExternalLinkAlt
} from 'react-icons/fa';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import '../whats-new/WhatsNewPage.css';
import './WhatsNewAdmin.css';
import defaultBanners from '../whats-new/banners.json';

const ResponsiveGridLayout = WidthProvider(Responsive);
const STORAGE_KEY = 'imatrix_whatsNew_banners';
const API_URL = 'https://script.google.com/macros/s/AKfycbxbJPCI3ddxobjFaup_wFFJ_wtX0v7rgWPiKLvehnQfPK-6o-K60tCzJFXjZkYc5MrVXA/exec'; // Paste your Google Apps Script Web App URL here
const generateId = () => `wn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const DEFAULT_BANNER = {
  id: '',
  type: 'banner',
  title: '',
  subtitle: '',
  description: '',
  imageUrl: '',
  linkUrl: '',
  linkLabel: 'Learn More',
  badge: '',
  badgeColor: '#ef4444',
  bgColor: '#ffffff',
  textColor: '#1e293b',
  contentLayout: 'image-top',
  imageFit: 'contain',
  imageSize: 50,
  pinned: false,
  visible: true,
  expiresAt: '',
  createdAt: new Date().toISOString(),
  // Free-form grid layout defaults (12 columns total)
  x: 0,
  y: 0,
  w: 4,
  h: 12
};

const TYPES = [
  { value: 'banner', label: '🖼️ Banner' },
  { value: 'offer', label: '🏷️ Offer' },
  { value: 'announcement', label: '📣 Announcement' },
  { value: 'card', label: '🆕 Update' },
];

const CONTENT_LAYOUTS = [
  { value: 'image-top', label: '▲', desc: 'Image Top' },
  { value: 'image-left', label: '◀', desc: 'Image Left' },
  { value: 'image-right', label: '▶', desc: 'Image Right' },
  { value: 'text-only', label: '≡', desc: 'Text Only' },
];

const PRESET_BG_COLORS = [
  '#ffffff', '#f8fafc', '#f1f5f9', '#cbd5e1',
  '#6b7a52', '#3f4e2f', '#4a5568', '#1e293b',
  '#0f172a', '#18181b', '#000000',
  '#fffbeb', '#f0fdf4', '#fff5f5', '#f0f9ff'
];

const PRESET_TEXT_COLORS = [
  '#ffffff', '#f8fafc', '#f1f5f9', '#cbd5e1',
  '#0f172a', '#1e293b', '#334155', '#475569',
  '#6b7a52', '#78350f', '#166534', '#9b1c1c'
];

const THEME_PRESETS = [
  { name: 'Light Slate', bg: '#ffffff', text: '#0f172a' },
  { name: 'Dark Slate', bg: '#0f172a', text: '#f8fafc' },
  { name: 'Brand Sage', bg: '#6b7a52', text: '#ffffff' },
  { name: 'Soft Sage', bg: '#eef1eb', text: '#3f4e2f' },
  { name: 'Warm Amber', bg: '#fffbeb', text: '#78350f' },
  { name: 'Cool Sky', bg: '#f0f9ff', text: '#075985' },
  { name: 'Soft Rose', bg: '#fff5f5', text: '#9b1c1c' }
];

const dispatchUpdate = () => window.dispatchEvent(new CustomEvent('whatsNewUpdated'));

const WhatsNewAdmin = () => {
  const [banners, setBanners] = useState([]);
  const [editingBanner, setEditingBanner] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [hasUnsavedLayout, setHasUnsavedLayout] = useState(false);
  const importRef = useRef(null);

  // Load
  useEffect(() => {
    const load = async () => {
      if (API_URL) {
        try {
          const res = await fetch(API_URL);
          const data = await res.json();
          if (Array.isArray(data)) {
            setBanners(data);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            return;
          }
        } catch (err) {
          console.error("Error loading layout from Google Drive API:", err);
          toast.info("Could not connect to Google Drive API. Loading local draft instead.");
        }
      }
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const data = raw ? JSON.parse(raw) : defaultBanners;
        setBanners(Array.isArray(data) ? data : defaultBanners);
      } catch {
        setBanners(defaultBanners);
      }
    };
    load();
  }, []);

  // Save
  const save = async (data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setBanners(data);
    dispatchUpdate();

    if (API_URL) {
      const loadingToast = toast.loading("Saving to Google Drive...");
      try {
        await fetch(API_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        });
        toast.update(loadingToast, {
          render: "Saved live to Google Drive successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000
        });
      } catch (err) {
        console.error("Error saving layout to Google Drive:", err);
        toast.update(loadingToast, {
          render: "Failed to save to Google Drive. Saved in browser instead.",
          type: "error",
          isLoading: false,
          autoClose: 4000
        });
      }
    }
  };

  const handleResetToDefault = () => {
    if (window.confirm("Are you sure you want to reset all banners to the default starter set? This will discard your local drafts.")) {
      localStorage.removeItem(STORAGE_KEY);
      setBanners(defaultBanners);
      setHasUnsavedLayout(false);
      dispatchUpdate();
      toast.success("Reset to default configuration.");
    }
  };

  const handleAddNew = () => {
    // Find bottom to avoid overlapping immediately
    let maxY = 0;
    banners.forEach(b => { if ((b.y || 0) + (b.h || 12) > maxY) maxY = (b.y || 0) + (b.h || 12); });

    const newBanner = {
      ...DEFAULT_BANNER,
      id: generateId(),
      createdAt: new Date().toISOString(),
      x: 0,
      y: maxY,
      w: 4,
      h: 12
    };
    setEditingBanner(newBanner);
    setIsDrawerOpen(true);
  };

  const handleEdit = (banner) => {
    setEditingBanner({ ...banner });
    setIsDrawerOpen(true);
  };

  const handleSave = () => {
    if (!editingBanner.title.trim()) {
      toast.error('Title is required!');
      return;
    }
    const exists = banners.find(b => b.id === editingBanner.id);
    let updated;
    if (exists) {
      updated = banners.map(b => b.id === editingBanner.id ? editingBanner : b);
      toast.success('Banner updated!');
    } else {
      updated = [...banners, editingBanner];
      toast.success('New banner added to bottom of grid!');
    }
    save(updated);
    setIsDrawerOpen(false);
    setEditingBanner(null);
  };

  const handleDelete = (id) => {
    const updated = banners.filter(b => b.id !== id);
    save(updated);
    setDeleteConfirm(null);
    toast.success('Banner deleted.');
    if (editingBanner?.id === id) {
      setIsDrawerOpen(false);
      setEditingBanner(null);
    }
  };

  const handleToggleVisible = (id) => {
    const updated = banners.map(b => b.id === id ? { ...b, visible: !b.visible } : b);
    save(updated);
  };

  const handleTogglePin = (id) => {
    const updated = banners.map(b => b.id === id ? { ...b, pinned: !b.pinned } : b);
    save(updated);
  };

  const handleLayoutChange = (layout, layouts) => {
    // RGL gives us an array of objects: {i: "id", x, y, w, h}
    // We must merge these into our banners array
    const updatedBanners = banners.map(banner => {
      const match = layout.find(l => l.i === banner.id);
      if (match) {
        return {
          ...banner,
          x: match.x,
          y: match.y,
          w: match.w,
          h: match.h
        };
      }
      return banner;
    });
    setBanners(updatedBanners);
    setHasUnsavedLayout(true);
  };

  const handleSaveLayout = () => {
    save(banners);
    setHasUnsavedLayout(false);
    toast.success('Grid layout saved successfully!');
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(banners, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `imatrix_whatsNew_FreeForm_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported successfully!');
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!Array.isArray(data)) throw new Error();
        save(data);
        toast.success(`Imported ${data.length} banners!`);
      } catch {
        toast.error('Invalid JSON file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image max size 2MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setEditingBanner(prev => ({ ...prev, imageUrl: ev.target.result }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const setField = (key, value) => {
    setEditingBanner(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="wna-root">
      <ToastContainer position="top-right" autoClose={3000} />

      <header className="wna-header">
        <div className="wna-header-title">
          <div className="wna-live-badge"><span className="wna-live-dot" /> Canvas Active</div>
          <h1>What's New — Free-form CMS</h1>
          <p>Drag, drop, and resize banners anywhere on the grid.</p>
        </div>
        <div className="wna-header-actions">
          <button className="wna-btn wna-btn-ghost" onClick={handleResetToDefault}>🔄 Reset</button>
          <button className="wna-btn wna-btn-ghost" onClick={handleExport}><FaDownload /> Export</button>
          <label className="wna-btn wna-btn-ghost">
            <FaUpload /> Import
            <input ref={importRef} type="file" accept=".json" hidden onChange={handleImport} />
          </label>
          <a href="/whats-new" target="_blank" rel="noopener noreferrer" className="wna-btn wna-btn-ghost">
            <FaExternalLinkAlt /> View Live
          </a>
          {hasUnsavedLayout && (
            <button className="wna-btn wna-btn-save-layout pulsing" onClick={handleSaveLayout}>
              💾 Save Layout
            </button>
          )}
          <button className="wna-btn wna-btn-primary" onClick={handleAddNew}>
            <FaPlus /> Add Banner
          </button>
        </div>
      </header>

      <nav className="wna-subnav">
        <Link to="/admin/superadmin" className="wna-subnav-link">Careers Portal</Link>
        <Link to="/admin/contactus" className="wna-subnav-link">Contact Messages</Link>
        <Link to="/admin/whatsnew" className="wna-subnav-link active">What's New Free-form CMS</Link>
      </nav>

      {/* Grid Canvas */}
      <section className="wna-canvas-section">
        {hasUnsavedLayout && (
          <div className="wna-unsaved-banner-alert">
            <span>⚠️ You have unsaved layout modifications. Click <strong>"Save Layout"</strong> to publish them to the live page.</span>
            <button className="wna-btn-save-layout-small" onClick={handleSaveLayout}>Save Layout</button>
          </div>
        )}
        {banners.length === 0 ? (
          <div className="wna-empty">
            <span>📐</span>
            <h3>The canvas is empty</h3>
            <p>Click "Add Banner" to start designing your layout.</p>
          </div>
        ) : (
          <div className="wna-canvas-wrapper">
            <ResponsiveGridLayout
              className="layout"
              layouts={{ lg: banners.map(b => ({ i: b.id, x: b.x || 0, y: b.y || 0, w: b.w || 4, h: b.h || 12 })) }}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              rowHeight={10}
              onLayoutChange={handleLayoutChange}
              draggableHandle=".wna-tile-drag-handle"
            >
              {banners.map(banner => {
                const layoutClass = `wn-layout-${banner.contentLayout || 'image-top'}`;
                const hasImg = banner.imageUrl && banner.contentLayout !== 'text-only';
                const isExpired = banner.expiresAt && new Date(banner.expiresAt) < new Date();
                const isNewBanner = banner.createdAt && (new Date() - new Date(banner.createdAt)) / (1000 * 60 * 60 * 24) <= 7;
                const hasBadges = banner.pinned || isNewBanner || banner.badge;
                const noImage = !hasImg;

                const imgFit = banner.imageFit || 'contain';
                const imgSize = banner.imageSize !== undefined ? banner.imageSize : 50;

                const wrapStyle = {};
                const bodyStyle = {};

                if (hasImg) {
                  if (banner.contentLayout === 'image-top') {
                    wrapStyle.height = `${imgSize}%`;
                    bodyStyle.height = `${100 - imgSize}%`;
                    if (imgSize === 100) {
                      bodyStyle.position = 'absolute';
                      bodyStyle.bottom = 0;
                      bodyStyle.left = 0;
                      bodyStyle.right = 0;
                      bodyStyle.height = 'auto';
                      bodyStyle.background = 'rgba(0,0,0,0.7)';
                      bodyStyle.color = '#ffffff';
                      bodyStyle.backdropFilter = 'blur(8px)';
                    }
                  } else if (banner.contentLayout === 'image-left' || banner.contentLayout === 'image-right') {
                    wrapStyle.width = `${imgSize}%`;
                    bodyStyle.width = `${100 - imgSize}%`;
                    if (imgSize === 100) {
                      bodyStyle.position = 'absolute';
                      bodyStyle.bottom = 0;
                      bodyStyle.left = 0;
                      bodyStyle.right = 0;
                      bodyStyle.height = 'auto';
                      bodyStyle.background = 'rgba(0,0,0,0.7)';
                      bodyStyle.color = '#ffffff';
                      bodyStyle.backdropFilter = 'blur(8px)';
                    }
                  }
                }

                return (
                  <div key={banner.id} className={`wna-grid-item ${!banner.visible ? 'wna-item-hidden' : ''}`}>
                    <div
                      className={`wn-tile ${layoutClass} ${banner.pinned ? 'wn-tile-pinned' : ''} ${noImage ? 'wn-tile-no-image' : ''} ${hasBadges ? 'wn-tile-has-badges' : ''}`}
                      style={{
                        background: banner.bgColor || '#fff',
                        color: banner.textColor || '#1e293b',
                        border: banner.pinned ? '2px solid var(--brand)' : '1px solid var(--border)',
                        height: '100%'
                      }}
                    >
                      {/* Drag Handle Top Bar */}
                      <div className="wna-tile-drag-handle">
                        ☰ Drag to Move
                      </div>

                      {/* Admin Controls Overlay */}
                      <div className="wna-tile-controls">
                        <button onClick={() => handleToggleVisible(banner.id)} title="Toggle Visibility">
                          {banner.visible ? <FaEye /> : <FaEyeSlash />}
                        </button>
                        <button onClick={() => handleEdit(banner)} title="Edit Banner"><FaEdit /></button>
                        <button onClick={() => setDeleteConfirm(banner.id)} title="Delete"><FaTrashAlt /></button>
                      </div>

                      {banner.badge && (
                        <span className="wn-custom-badge" style={{ background: banner.badgeColor }}>
                          {banner.badge}
                        </span>
                      )}

                      {hasImg && (
                        <div className="wn-tile-img-wrap" style={wrapStyle}>
                          <img src={banner.imageUrl} alt="" className="wn-tile-img-blur" />
                          <img src={banner.imageUrl} alt="" className="wn-tile-img" style={{ objectFit: imgFit }} />
                        </div>
                      )}

                      <div className="wn-tile-body" style={bodyStyle}>
                        {isExpired && <div style={{ color: 'red', fontSize: '0.7rem', fontWeight: 800 }}>EXPIRED</div>}
                        <h3 className="wn-tile-title">{banner.title || 'Untitled Banner'}</h3>
                        {banner.subtitle && <p className="wn-tile-subtitle">{banner.subtitle}</p>}
                        {banner.description && <p className="wn-tile-desc">{banner.description}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </ResponsiveGridLayout>
          </div>
        )}
      </section>

      {/* Drawer */}
      {isDrawerOpen && editingBanner && (
        <div className="wna-overlay" onClick={() => setIsDrawerOpen(false)}>
          <aside className="wna-drawer" onClick={e => e.stopPropagation()}>
            <div className="wna-drawer-header">
              <h2>{banners.find(b => b.id === editingBanner.id) ? 'Edit Banner' : 'New Banner'}</h2>
              <button className="wna-drawer-save-btn" onClick={handleSave}><FaSave /> Save</button>
            </div>
            <div className="wna-drawer-body">
              <div className="wna-form-section">
                <label className="wna-label">Type</label>
                <div className="wna-layout-selector">
                  {TYPES.map(t => (
                    <button key={t.value} className={`wna-layout-btn ${editingBanner.type === t.value ? 'selected' : ''}`} onClick={() => setField('type', t.value)}>
                      <span className="wna-layout-desc" style={{ fontSize: '0.85rem' }}>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="wna-form-section">
                <label className="wna-label">Title <span className="wna-required">*</span></label>
                <input className="wna-input" placeholder="e.g., Summer Sale 50% Off!" value={editingBanner.title} onChange={e => setField('title', e.target.value)} />
              </div>
              <div className="wna-form-section">
                <label className="wna-label">Subtitle</label>
                <input className="wna-input" placeholder="e.g., Limited time offer ends Friday" value={editingBanner.subtitle} onChange={e => setField('subtitle', e.target.value)} />
              </div>
              <div className="wna-form-section">
                <label className="wna-label">Description</label>
                <textarea className="wna-textarea" placeholder="Add some extra details here..." value={editingBanner.description} onChange={e => setField('description', e.target.value)} />
              </div>

              <div className="wna-form-section wna-form-row">
                <div className="wna-form-col">
                  <label className="wna-label">Badge Text</label>
                  <input className="wna-input" placeholder="e.g., HOT, NEW" value={editingBanner.badge} onChange={e => setField('badge', e.target.value)} />
                </div>
                <div className="wna-form-col">
                  <label className="wna-label">Badge Color</label>
                  <input type="color" className="wna-color-picker" value={editingBanner.badgeColor} onChange={e => setField('badgeColor', e.target.value)} />
                </div>
              </div>

              <div className="wna-form-section">
                <label className="wna-label">Image</label>
                <div className="wna-image-upload-area">
                  {editingBanner.imageUrl ? (
                    <div className="wna-img-preview-wrap">
                      <img src={editingBanner.imageUrl} alt="" className="wna-img-preview" />
                      <button className="wna-img-remove" onClick={() => setField('imageUrl', '')}><FaTimes /></button>
                    </div>
                  ) : (
                    <label className="wna-img-drop-zone">
                      <FaImage /><span>Upload</span>
                      <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                    </label>
                  )}
                </div>
                <input className="wna-input" placeholder="Or paste URL" value={editingBanner.imageUrl?.startsWith('data:') ? '' : editingBanner.imageUrl} onChange={e => setField('imageUrl', e.target.value)} />
              </div>

              {editingBanner.imageUrl && (
                <div className="wna-form-section wna-form-row">
                  <div className="wna-form-col">
                    <label className="wna-label">Image Fit Mode</label>
                    <div className="wna-layout-selector">
                      <button
                        type="button"
                        className={`wna-layout-btn ${editingBanner.imageFit !== 'cover' ? 'selected' : ''}`}
                        onClick={() => setField('imageFit', 'contain')}
                      >
                        <span className="wna-layout-desc" style={{ fontSize: '0.85rem' }}>Fit (Full Image)</span>
                      </button>
                      <button
                        type="button"
                        className={`wna-layout-btn ${editingBanner.imageFit === 'cover' ? 'selected' : ''}`}
                        onClick={() => setField('imageFit', 'cover')}
                      >
                        <span className="wna-layout-desc" style={{ fontSize: '0.85rem' }}>Fill (Crop)</span>
                      </button>
                    </div>
                  </div>
                  <div className="wna-form-col">
                    <label className="wna-label">Image Size Ratio ({editingBanner.imageSize !== undefined ? editingBanner.imageSize : 50}%)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        step="5"
                        value={editingBanner.imageSize !== undefined ? editingBanner.imageSize : 50}
                        onChange={e => setField('imageSize', parseInt(e.target.value))}
                        className="wna-slider"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="wna-form-section wna-form-row">
                <div className="wna-form-col">
                  <label className="wna-label">Link URL</label>
                  <input className="wna-input" placeholder="https://..." value={editingBanner.linkUrl} onChange={e => setField('linkUrl', e.target.value)} />
                </div>
                <div className="wna-form-col">
                  <label className="wna-label">Button Label</label>
                  <input className="wna-input" placeholder="e.g., Learn More" value={editingBanner.linkLabel} onChange={e => setField('linkLabel', e.target.value)} />
                </div>
              </div>

              <div className="wna-form-section">
                <label className="wna-label">Content Layout</label>
                <div className="wna-layout-selector">
                  {CONTENT_LAYOUTS.map(l => (
                    <button key={l.value} className={`wna-layout-btn ${editingBanner.contentLayout === l.value ? 'selected' : ''}`} onClick={() => setField('contentLayout', l.value)}>
                      <span className="wna-layout-symbol">{l.label}</span>
                      <span className="wna-layout-desc">{l.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="wna-form-section">
                <label className="wna-label">Quick Themes</label>
                <div className="wna-presets-grid">
                  {THEME_PRESETS.map((theme, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="wna-preset-theme-btn"
                      style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.text}22` }}
                      onClick={() => {
                        setField('bgColor', theme.bg);
                        setField('textColor', theme.text);
                      }}
                    >
                      {theme.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="wna-form-section">
                <label className="wna-label">Background Color</label>
                <div className="wna-color-row">
                  <input type="color" className="wna-color-picker" value={editingBanner.bgColor} onChange={e => setField('bgColor', e.target.value)} />
                  <div className="wna-presets-list">
                    {PRESET_BG_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`wna-color-swatch ${editingBanner.bgColor === color ? 'active' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setField('bgColor', color)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="wna-form-section">
                <label className="wna-label">Text Color</label>
                <div className="wna-color-row">
                  <input type="color" className="wna-color-picker" value={editingBanner.textColor} onChange={e => setField('textColor', e.target.value)} />
                  <div className="wna-presets-list">
                    {PRESET_TEXT_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`wna-color-swatch ${editingBanner.textColor === color ? 'active' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setField('textColor', color)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="wna-form-section wna-toggles-row">
                <div className="wna-toggle-group">
                  <label className="wna-label">Visible</label>
                  <button className={`wna-toggle-btn ${editingBanner.visible ? 'on' : 'off'}`} onClick={() => setField('visible', !editingBanner.visible)}>
                    {editingBanner.visible ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div className="wna-toggle-group">
                  <label className="wna-label">Pinned</label>
                  <button className={`wna-toggle-btn ${editingBanner.pinned ? 'on' : 'off'}`} onClick={() => setField('pinned', !editingBanner.pinned)}>
                    {editingBanner.pinned ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>

              <div className="wna-form-section" style={{ marginTop: '16px' }}>
                <label className="wna-label">Live Preview</label>
                {(() => {
                  const pFit = editingBanner.imageFit || 'contain';
                  const pSize = editingBanner.imageSize !== undefined ? editingBanner.imageSize : 50;
                  const pWrapStyle = {};
                  const pBodyStyle = {};
                  const hasPImg = editingBanner.imageUrl && editingBanner.contentLayout !== 'text-only';

                  if (hasPImg) {
                    if (editingBanner.contentLayout === 'image-top') {
                      pWrapStyle.height = `${pSize}%`;
                      pBodyStyle.height = `${100 - pSize}%`;
                      if (pSize === 100) {
                        pBodyStyle.position = 'absolute';
                        pBodyStyle.bottom = 0;
                        pBodyStyle.left = 0;
                        pBodyStyle.right = 0;
                        pBodyStyle.height = 'auto';
                        pBodyStyle.background = 'rgba(0,0,0,0.7)';
                        pBodyStyle.color = '#ffffff';
                        pBodyStyle.backdropFilter = 'blur(8px)';
                      }
                    } else if (editingBanner.contentLayout === 'image-left' || editingBanner.contentLayout === 'image-right') {
                      pWrapStyle.width = `${pSize}%`;
                      pBodyStyle.width = `${100 - pSize}%`;
                      if (pSize === 100) {
                        pBodyStyle.position = 'absolute';
                        pBodyStyle.bottom = 0;
                        pBodyStyle.left = 0;
                        pBodyStyle.right = 0;
                        pBodyStyle.height = 'auto';
                        pBodyStyle.background = 'rgba(0,0,0,0.7)';
                        pBodyStyle.color = '#ffffff';
                        pBodyStyle.backdropFilter = 'blur(8px)';
                      }
                    }
                  }

                  const previewLayoutClass = `wn-layout-${editingBanner.contentLayout || 'image-top'}`;

                  return (
                    <div className={`wna-mini-preview ${previewLayoutClass}`} style={{ background: editingBanner.bgColor, color: editingBanner.textColor, position: 'relative', height: '240px', display: 'flex' }}>
                      {hasPImg && (
                        <div className="wn-tile-img-wrap wna-preview-img-wrap" style={pWrapStyle}>
                          <img src={editingBanner.imageUrl} alt="" className="wn-tile-img-blur" />
                          <img src={editingBanner.imageUrl} alt="" className="wn-tile-img" style={{ objectFit: pFit }} />
                        </div>
                      )}
                      <div className="wna-preview-body" style={pBodyStyle}>
                        {editingBanner.badge && (
                          <span className="wna-preview-badge" style={{ background: editingBanner.badgeColor || '#ef4444' }}>
                            {editingBanner.badge}
                          </span>
                        )}
                        <h4 className="wna-preview-title" style={{ margin: '4px 0' }}>{editingBanner.title || 'Preview Title'}</h4>
                        {editingBanner.subtitle && <p className="wna-preview-sub" style={{ margin: 0 }}>{editingBanner.subtitle}</p>}
                        {editingBanner.linkLabel && (
                          <span className="wna-preview-cta" style={{ color: 'var(--brand)' }}>{editingBanner.linkLabel} →</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="wna-drawer-footer">
              <button className="wna-btn wna-btn-ghost" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
              <button className="wna-btn wna-btn-primary" onClick={handleSave}>Save</button>
            </div>
          </aside>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="wna-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="wna-confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Delete banner?</h3>
            <div className="wna-confirm-actions">
              <button className="wna-btn wna-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="wna-btn wna-btn-danger" onClick={() => handleDelete(deleteConfirm)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsNewAdmin;
